<template lang="pug">
aside.sidebar.rounded-4.mt-4(:class="sidebarClasses")
  .sidebar__header(v-if="title")
    // Title is the dropdown trigger (uses EcreativArchetypesHeading)
    EcreativArchetypesHeading.sidebar__title-btn(
      tag="h3"
      size="4"
      class="text-white"
      role="button"
      tabindex="0"
      :id="titleId"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-controls="panelId"
      @click="toggle()"
      @keydown.space.prevent="toggle()"
      @keydown.enter.prevent="toggle()"
    )
      span {{ title }}
      // Optional chevron
      svg.sidebar__chevron(
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        width="20"
        height="20"
        fill="currentColor"
        :class="{ 'is-open': open }"
        aria-hidden="true"
        focusable="false"
      )
        path(d="M7.41 8.59 12 13.17l4.59-4.58 1.41 1.41L12 16 6 10l1.41-1.41z")

  // Links panel (opens/collapses with the title)
  Transition(name="accordion")
    div.sidebar__panel(
      v-show="!collapsible || open"
      :id="panelId"
      role="region"
      :aria-labelledby="titleId"
    )
      ul.sidebar__menu(v-if="links && links.length")
        li.sidebar__item(v-for="item in links" :key="item.id || item.text")
          EcreativMoleculesNavigationList(
            :title="item.text"
            :links="item.children || []"
            linkColor="white"
          )

  // Extra content slot
  slot
</template>

<script lang="ts" setup>
/* eslint-disable */
import { ref, computed } from 'vue';
import EcreativArchetypesHeading from '../archetypes/Heading';
import EcreativMoleculesNavigationList from './NavigationList.vue';
import type { NavigationNode } from 'data-types-config';

interface Props {
  links?: NavigationNode[];
  title?: string;
  background: 'solid' | 'transparent';
  collapsible?: boolean;
  defaultOpen?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  background: 'solid',
  collapsible: true,
  defaultOpen: false,
});

const sidebarClasses = computed(() => ({
  'sidebar--solid': props.background === 'solid',
  'sidebar--transparent': props.background === 'transparent',
  'sidebar--rounded': true,
}));

const open = ref<boolean>(!!props.defaultOpen);

const panelId = 'sidebar-links-panel';
const titleId = 'sidebar-title';

function toggle() {
  if (!props.collapsible) return;
  open.value = !open.value;
}
</script>

<style lang="scss" scoped>
$block: '.sidebar';

#{$block} {
  $block: &;

  &#{$block} {
    display: flex;
    flex-direction: column;
    gap: 16px;
    overflow-y: auto;
    padding: 25px;
    border: 1px solid rgb(182 109 182 / 25.2%);
    transition: background-color 0.3s ease;
    backdrop-filter: blur(10px);

    @include media-breakpoint-up(sm) {
      padding: 16px;
    }

    @include media-breakpoint-up(md) {
      padding: 24px;
    }
  }

  // The clickable heading
  .sidebar__title-btn {
    display: flex;
    gap: 12px;
    align-items: center;
    justify-content: space-between;
    margin: 0; // ensure no extra gaps
    cursor: pointer;
    user-select: none;
  }

  .sidebar__chevron {
    transition: transform 0.2s ease;

    &.is-open {
      transform: rotate(180deg);
    }
  }

  #{$block}__menu {
    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: center;
    margin: 0;
    padding: 0;
    list-style: none;
    text-align: center;

    @include media-breakpoint-up(sm) {
      flex-direction: row;
      gap: 32px;
      align-items: normal;
      text-align: start;
    }

    @include media-breakpoint-up(xl) {
      flex-direction: column;
    }
  }

  #{$block}__item {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
  }

  .sidebar__panel {
    overflow: hidden;
    padding: 0 4px 8px;
    border-radius: 8px;
  }

  /* Smooth expand/collapse */
  .accordion-enter-from,
  .accordion-leave-to {
    max-height: 0;
    opacity: 0;
  }

  .accordion-enter-to,
  .accordion-leave-from {
    max-height: 500px;
    opacity: 1;
  }

  .accordion-enter-active,
  .accordion-leave-active {
    transition:
      max-height 0.2s ease,
      opacity 0.2s ease;
  }
}

/* Modifiers */
#{$block}--solid {
  background-color: rgb(255 255 255 / 80%);
}
#{$block}--transparent {
  background-color: rgb(255 255 255 / 10%);
}
</style>
